import React, { useState, useRef, useCallback } from 'react';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, X, ChevronDown, Loader2, Sparkles, Trash2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { INMOCEAN_FILE_SCHEMAS } from '../config/fileSchemas';
import { InMoceanDataService } from '../services/InMoceanDataService';
import { InMoceanValidationService } from '../services/InMoceanValidationService';

// ============================================================================
// SMART DROP ZONE COMPONENT
// Auto-detects file types based on column headers
// ============================================================================
export default function SmartDropZone({ onUploadComplete, onUploadError, uploadHistory }) {
  const [dragOver, setDragOver] = useState(false);
  const [detectedFiles, setDetectedFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  // Get costing file schemas (exclude sync types)
  const costingSchemas = Object.entries(INMOCEAN_FILE_SCHEMAS)
    .filter(([key, schema]) => schema && schema.type !== 'sync')
    .reduce((acc, [key, schema]) => ({ ...acc, [key]: schema }), {});

  // ============================================================================
  // COLUMN MATCHING ALGORITHM
  // ============================================================================
  const normalizeColumn = (col) => {
    if (!col) return '';
    return col.toString().trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  };

  const calculateMatchScore = (fileHeaders, schema) => {
    if (!schema.columns || schema.columns.length === 0) {
      // For schemas with no defined columns (flexible), check required columns
      if (!schema.requiredColumns || schema.requiredColumns.length === 0) {
        return { score: 0, matched: 0, total: 0, requiredMatched: 0, requiredTotal: 0 };
      }
    }

    const normalizedFileHeaders = fileHeaders.map(normalizeColumn);
    
    // Match against all columns
    const allColumns = schema.columns || [];
    let matchedCount = 0;
    allColumns.forEach(expectedCol => {
      const normalizedExpected = normalizeColumn(expectedCol);
      if (normalizedFileHeaders.some(h => h === normalizedExpected || h.includes(normalizedExpected) || normalizedExpected.includes(h))) {
        matchedCount++;
      }
    });

    // Match against required columns (more weight)
    const requiredColumns = schema.requiredColumns || [];
    let requiredMatchedCount = 0;
    requiredColumns.forEach(reqCol => {
      const normalizedReq = normalizeColumn(reqCol);
      if (normalizedFileHeaders.some(h => h === normalizedReq || h.includes(normalizedReq) || normalizedReq.includes(h))) {
        requiredMatchedCount++;
      }
    });

    const columnScore = allColumns.length > 0 ? (matchedCount / allColumns.length) * 100 : 0;
    const requiredScore = requiredColumns.length > 0 ? (requiredMatchedCount / requiredColumns.length) * 100 : 100;
    
    // Weighted score: required columns are more important
    const weightedScore = (columnScore * 0.4) + (requiredScore * 0.6);

    return {
      score: Math.round(weightedScore),
      matched: matchedCount,
      total: allColumns.length,
      requiredMatched: requiredMatchedCount,
      requiredTotal: requiredColumns.length
    };
  };

  const detectFileType = (fileHeaders, fileName) => {
    const results = [];
    
    Object.entries(costingSchemas).forEach(([key, schema]) => {
      const matchResult = calculateMatchScore(fileHeaders, schema);
      results.push({
        key,
        schema,
        ...matchResult
      });
    });

    // Sort by score descending
    results.sort((a, b) => b.score - a.score);

    // Check for filename hints
    const lowerFileName = fileName.toLowerCase();
    const filenameHints = {
      'shipping': ['shipping', 'ship', 'container'],
      'style_hts': ['hts', 'style_hts', 'tariff', 'harmonized'],
      'freight_charges': ['freight', 'frt', 'ocean', 'air'],
      'production_orders': ['production', 'prod_ord', 'po_order'],
      'cost_sheet': ['cost', 'costing', 'price'],
      'duty_charges': ['duty', 'customs', 'tariff'],
      'po_terms': ['terms', 'po_terms', 'shipment'],
      'style_master': ['style', 'master', 'fineline'],
      'inventory': ['inventory', 'inv', 'qoh', 'stock'],
      'hts_updates': ['hts', 'tariff', 'duty', 'harmonized']
    };

    // Boost score if filename matches
    results.forEach(r => {
      const hints = filenameHints[r.key] || [];
      if (hints.some(hint => lowerFileName.includes(hint))) {
        r.score = Math.min(100, r.score + 15);
        r.filenameMatch = true;
      }
    });

    // Re-sort after filename boost
    results.sort((a, b) => b.score - a.score);

    return {
      bestMatch: results[0],
      allMatches: results.filter(r => r.score > 20),
      confidence: results[0]?.score >= 70 ? 'high' : results[0]?.score >= 40 ? 'medium' : 'low'
    };
  };

  // ============================================================================
  // FILE PARSING
  // ============================================================================
  const parseFileHeaders = async (file) => {
    return new Promise((resolve, reject) => {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          let headers = [];

          if (fileExtension === 'csv') {
            const text = e.target.result;
            const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
            if (lines.length > 0) {
              // Parse first row as headers
              const firstLine = lines[0];
              headers = firstLine.split(',').map(h => h.replace(/^"|"$/g, '').trim());
            }
          } else {
            // Excel file
            const workbook = XLSX.read(e.target.result, { 
              type: 'binary',
              sheetRows: 15 // Only read first 15 rows to find headers
            });

            if (workbook.SheetNames && workbook.SheetNames.length > 0) {
              const worksheet = workbook.Sheets[workbook.SheetNames[0]];
              const allRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });

              // Find row with most columns (likely the header row)
              let bestHeaderIndex = 0;
              let maxColumns = 0;
              for (let i = 0; i < Math.min(allRows.length, 10); i++) {
                const row = allRows[i] || [];
                const columnCount = row.filter(cell => cell !== null && String(cell).trim() !== '').length;
                if (columnCount > maxColumns) {
                  maxColumns = columnCount;
                  bestHeaderIndex = i;
                }
              }

              const headerRow = allRows[bestHeaderIndex] || [];
              headers = headerRow.map(h => String(h || '').trim()).filter(h => h !== '');
            }
          }

          resolve(headers);
        } catch (error) {
          reject(new Error(`Failed to parse ${file.name}: ${error.message}`));
        }
      };

      reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));

      if (fileExtension === 'csv') {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    });
  };

  // ============================================================================
  // FILE HANDLING
  // ============================================================================
  const processFiles = async (files) => {
    setProcessing(true);
    const detected = [];

    for (const file of files) {
      try {
        const headers = await parseFileHeaders(file);
        const detection = detectFileType(headers, file.name);

        detected.push({
          id: `${file.name}-${Date.now()}`,
          file,
          fileName: file.name,
          fileSize: formatFileSize(file.size),
          headers,
          headerCount: headers.length,
          detection,
          selectedType: detection.bestMatch?.key || null,
          status: 'pending'
        });
      } catch (error) {
        detected.push({
          id: `${file.name}-${Date.now()}`,
          file,
          fileName: file.name,
          fileSize: formatFileSize(file.size),
          headers: [],
          headerCount: 0,
          detection: { bestMatch: null, allMatches: [], confidence: 'error' },
          selectedType: null,
          status: 'error',
          error: error.message
        });
      }
    }

    setDetectedFiles(prev => [...prev, ...detected]);
    setProcessing(false);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files).filter(
      f => f.name.endsWith('.csv') || f.name.endsWith('.xlsx') || f.name.endsWith('.xls')
    );

    if (files.length > 0) {
      processFiles(files);
    }
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(
      f => f.name.endsWith('.csv') || f.name.endsWith('.xlsx') || f.name.endsWith('.xls')
    );

    if (files.length > 0) {
      processFiles(files);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTypeChange = (fileId, newType) => {
    setDetectedFiles(prev => prev.map(f => 
      f.id === fileId ? { ...f, selectedType: newType } : f
    ));
  };

  const removeFile = (fileId) => {
    setDetectedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const clearAll = () => {
    setDetectedFiles([]);
  };

  // ============================================================================
  // UPLOAD HANDLER
  // ============================================================================
  const handleUploadAll = async () => {
    const filesToUpload = detectedFiles.filter(f => f.selectedType && f.status !== 'success');
    
    if (filesToUpload.length === 0) return;

    setUploading(true);

    for (const fileItem of filesToUpload) {
      try {
        setDetectedFiles(prev => prev.map(f => 
          f.id === fileItem.id ? { ...f, status: 'uploading' } : f
        ));

        setUploadProgress(prev => ({ ...prev, [fileItem.id]: 0 }));

        // Validate
        const schema = costingSchemas[fileItem.selectedType];
        const validation = await InMoceanValidationService.validateFile(
          fileItem.file,
          fileItem.selectedType,
          (stage) => {
            // Update progress based on stage
            if (stage.includes('format')) setUploadProgress(prev => ({ ...prev, [fileItem.id]: 10 }));
            else if (stage.includes('content')) setUploadProgress(prev => ({ ...prev, [fileItem.id]: 30 }));
            else if (stage.includes('structure')) setUploadProgress(prev => ({ ...prev, [fileItem.id]: 50 }));
          }
        );

        if (!validation.isValid) {
          throw new Error(validation.errors[0] || 'Validation failed');
        }

        setUploadProgress(prev => ({ ...prev, [fileItem.id]: 70 }));

        // Upload
        const result = await InMoceanDataService.uploadAndProcessFile(
          fileItem.file,
          fileItem.selectedType,
          validation.parsedData
        );

        setUploadProgress(prev => ({ ...prev, [fileItem.id]: 100 }));

        setDetectedFiles(prev => prev.map(f => 
          f.id === fileItem.id ? { ...f, status: 'success' } : f
        ));

        if (onUploadComplete) {
          onUploadComplete(fileItem.selectedType, result);
        }

      } catch (error) {
        setDetectedFiles(prev => prev.map(f => 
          f.id === fileItem.id ? { ...f, status: 'error', error: error.message } : f
        ));

        if (onUploadError) {
          onUploadError(fileItem.selectedType, error);
        }
      }
    }

    setUploading(false);
  };

  // ============================================================================
  // HELPERS
  // ============================================================================
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getConfidenceBadge = (confidence, score) => {
    if (confidence === 'high') {
      return <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">✓ {score}% match</span>;
    }
    if (confidence === 'medium') {
      return <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">⚠ {score}% match</span>;
    }
    return <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-xs font-medium">? Low match</span>;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
        return <Loader2 size={16} className="text-blue-500 animate-spin" />;
      case 'success':
        return <CheckCircle size={16} className="text-emerald-500" />;
      case 'error':
        return <AlertCircle size={16} className="text-rose-500" />;
      default:
        return null;
    }
  };

  const pendingCount = detectedFiles.filter(f => f.selectedType && f.status === 'pending').length;
  const successCount = detectedFiles.filter(f => f.status === 'success').length;

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
        onClick={() => fileInputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
          ${dragOver 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-slate-300 bg-gradient-to-br from-slate-50 to-white hover:border-blue-300 hover:bg-blue-50/30'
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".csv,.xlsx,.xls"
          onChange={handleFileSelect}
          className="hidden"
        />

        <div className="flex flex-col items-center gap-3">
          <div className={`p-4 rounded-full ${dragOver ? 'bg-blue-100' : 'bg-slate-100'}`}>
            {processing ? (
              <Loader2 size={32} className="text-blue-500 animate-spin" />
            ) : (
              <Sparkles size={32} className={dragOver ? 'text-blue-600' : 'text-slate-400'} />
            )}
          </div>
          
          <div>
            <p className={`text-lg font-medium ${dragOver ? 'text-blue-700' : 'text-slate-700'}`}>
              {processing ? 'Analyzing files...' : 'Drop files here for smart upload'}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Drop multiple files at once — we'll auto-detect the type for each
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-400">
            <FileSpreadsheet size={14} />
            <span>CSV, XLSX, XLS files supported</span>
          </div>
        </div>
      </div>

      {/* Detected Files List */}
      {detectedFiles.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="font-medium text-slate-700">
                {detectedFiles.length} file{detectedFiles.length > 1 ? 's' : ''} detected
              </h3>
              {successCount > 0 && (
                <span className="text-xs text-emerald-600 font-medium">
                  {successCount} uploaded
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearAll}
                className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
              >
                <Trash2 size={12} />
                Clear all
              </button>
            </div>
          </div>

          {/* File Cards */}
          <div className="divide-y divide-slate-100">
            {detectedFiles.map((item) => (
              <div 
                key={item.id} 
                className={`p-4 ${item.status === 'success' ? 'bg-emerald-50/50' : item.status === 'error' ? 'bg-rose-50/50' : ''}`}
              >
                <div className="flex items-start justify-between gap-4">
                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusIcon(item.status)}
                      <span className="font-medium text-slate-800 truncate">{item.fileName}</span>
                      <span className="text-xs text-slate-400">{item.fileSize}</span>
                    </div>

                    {item.status === 'error' && item.error && (
                      <p className="text-xs text-rose-600 mt-1">{item.error}</p>
                    )}

                    {item.status === 'uploading' && (
                      <div className="mt-2">
                        <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 transition-all duration-300"
                            style={{ width: `${uploadProgress[item.id] || 0}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {item.detection.confidence !== 'error' && item.status !== 'success' && (
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="text-xs text-slate-500">{item.headerCount} columns detected</span>
                        {item.detection.bestMatch && (
                          <>
                            <span className="text-slate-300">•</span>
                            {getConfidenceBadge(item.detection.confidence, item.detection.bestMatch.score)}
                          </>
                        )}
                        {item.detection.bestMatch?.filenameMatch && (
                          <span className="text-xs text-blue-600">📁 Filename match</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Type Selector */}
                  <div className="flex items-center gap-2">
                    {item.status !== 'success' && (
                      <>
                        <select
                          value={item.selectedType || ''}
                          onChange={(e) => handleTypeChange(item.id, e.target.value)}
                          disabled={item.status === 'uploading'}
                          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[180px]"
                        >
                          <option value="">Select file type...</option>
                          {Object.entries(costingSchemas).map(([key, schema]) => (
                            <option key={key} value={key}>
                              {schema.icon} {schema.name}
                            </option>
                          ))}
                        </select>

                        <button
                          onClick={() => removeFile(item.id)}
                          disabled={item.status === 'uploading'}
                          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                        >
                          <X size={16} />
                        </button>
                      </>
                    )}

                    {item.status === 'success' && (
                      <span className="text-sm text-emerald-600 font-medium flex items-center gap-1">
                        <CheckCircle size={14} />
                        Uploaded to {costingSchemas[item.selectedType]?.name}
                      </span>
                    )}
                  </div>
                </div>

                {/* Alternative Matches */}
                {item.detection.allMatches.length > 1 && item.status === 'pending' && (
                  <details className="mt-3">
                    <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                      Show other possible matches ({item.detection.allMatches.length - 1} more)
                    </summary>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {item.detection.allMatches.slice(1, 5).map((match) => (
                        <button
                          key={match.key}
                          onClick={() => handleTypeChange(item.id, match.key)}
                          className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-slate-600"
                        >
                          {match.schema.icon} {match.schema.name} ({match.score}%)
                        </button>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>

          {/* Upload Button */}
          {pendingCount > 0 && (
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
              <button
                onClick={handleUploadAll}
                disabled={uploading}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                {uploading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Upload {pendingCount} file{pendingCount > 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}








